import authService from '../../api/auth';
import {loading, handleError, alert, storeLocal, destroyLocal, getLocal} from '@/state/generators/helpers';
import {rememberEmbed} from '@/utils/embed';

export const state = {
    permissions: getLocal('permissions') || [],
    admin: getLocal('admin') || false,
    errors: {},
    loading: false,
    service: authService(''),
    authenticated: !!getLocal('authenticated'),
    // kétfaktoros belépés: a függő belépés állapota a jelszó után (null = nincs)
    twoFactor: null,
}

export const mutations = {
    'SET_AUTHENTICATED': function (state, boolean) {
        state.authenticated = boolean
    },

    'SET_TWO_FACTOR': function (state, challenge = null) {
        state.twoFactor = challenge
    },

    'SET_LOADING': function (state, boolean = true) {
        state.loading = boolean
    },

    'SET_ERRORS': function(state, errors) {
        state.errors = errors
    },

    'SET_ERROR': function(state, error) {
        const errors = Object.assign({}, state.errors)

        Object.keys(error).forEach(key => {
            errors[key]
                ? errors[key].push(error[key])
                : errors[key] = Array(error[key])
        })

        state.errors = errors
    },

    'SET_PERMISSIONS': function (state, permissions = []) {
        storeLocal('permissions', permissions)
        state.permissions = permissions
    },

    'SET_ADMIN': function (state, boolean) {
        storeLocal('admin', boolean)
        state.admin = boolean
    },
}

export const getters = {
    loading: function (state) {
        return !!state.loading
    },

    authenticated: function (state) {
        return state.authenticated
    },

    twoFactor: function (state) {
        return state.twoFactor
    },

    states: function (state) {
        const states = {};

        Object.keys(state.errors).forEach(key => {
                states[key] = false
            }
        )

        return states
    },

    errors: function (state) {
        const errors = Object.assign({}, (state.errors || {}))

        Object.keys(errors).forEach(key => {
            errors[key] = errors[key].join(' ')
        })

        return errors;
    },

    permissions: function (state) {
        return JSON.parse(JSON.stringify(state.permissions))
    },

    can: (state, getters, rootGetters) => (permission) => {
        if (permission === true) {
            return true;
        }

        const tenantContext = rootGetters['user'].authenticated?.tenant_context;

        // A szerver verify()-jának tükre: intézményi jog belépés nélkül nem jár. Belépve az írás-gombok is
        // látszanak: a szerver az írást csak-olvasásnál `tenant_read_only`-val utasítja el, és ekkor a felugró
        // ablak kéri a bekapcsolást, utána a mentés magától lefut (api/axios.js, tenant-access-prompt).
        if (tenantContext?.requires_entry && String(permission).startsWith('tenant.') && !tenantContext.entered) {
            return false;
        }

        if (rootGetters['user'].authenticated?.is_developer) {
            return true;
        }

        if (rootGetters['user'].authenticated?.is_admin && rootGetters['user'].authenticated?.is_global) {
            return true;
        }

        if (rootGetters['user'].authenticated?.is_admin && permission.startsWith('tenant')) {
            return true;
        }

        return getters.permissions.includes(permission);
    },

    admin: function (state) {
        return !!state.admin
    },
}

export const actions = {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    // eslint-disable-next-line no-unused-vars
    init({ dispatch }) {
        dispatch('validate')
    },

    addError: function ({commit}, payload) {
        commit('SET_ERROR', payload)
    },

    login: async function ({state, commit, dispatch}, payload) {
        loading(commit)

        /*if (getters.authenticated) {
            dispatch('logout')
            /!*loading(commit, false)
            const message = this.$t('auth.logged_in').ucFirst()
            alert(commit, message, 'warning', 'warning')
            throw new Error(message)*!/
        }*/

        destroyLocal('accessToken')
        destroyLocal('permissions')
        destroyLocal('admin')
        commit('SET_PERMISSIONS', []);
        commit('SET_ADMIN', false)

        // Belépés előzetes kilépés NÉLKÜL: egy kijelentkezett böngészőben a kilépés 401-et adott, és a
        // globális kezelő a belépés közben a belépőre dobott. Ha a böngészőben még él egy munkamenet
        // (pl. egy másik fülön), a szerver 409 `already_authenticated`-et ad, és az axios kilép, majd
        // egyszer újraküldi a belépést (`api/axios.js`).
        commit('SET_TWO_FACTOR', null)

        return state.service.csrf()
            .then(() => state.service.login(payload))
            .then(async (response) => {
                // kétfaktoros fiók: a jelszó rendben, de még NINCS bejelentkezett munkamenet; a
                // belépő a kód-képernyőt mutatja, a befejezést a `twoFactorVerify` végzi
                if (response?.two_factor === true) {
                    commit('SET_TWO_FACTOR', response)
                    loading(commit, false);
                    return response;
                }

                await dispatch('completeSignIn');
                loading(commit, false);
                return response;
            }).catch((e) => {
                if (e?.response?.status === 302) {
                    commit('SET_AUTHENTICATED', true)
                    storeLocal('authenticated', true)
                    dispatch('user/authenticated', {}, {root: true})
                    dispatch('app/configuration', {}, {root: true})
                    dispatch('permissions')
                    loading(commit, false)
                } else {
                    handleError(e, commit)
                }
            })
    },

    /**
     * A sikeres belépés utáni közös rész (jelszó után, vagy a kétfaktoros kód után): a felhasználó,
     * a beállítások és a jogosultságok betöltése.
     */
    completeSignIn: async function ({commit, dispatch}) {
        commit('SET_AUTHENTICATED', true)
        storeLocal('authenticated', true);
        await Promise.all([
            // A nyelvet SZÁNDÉKOSAN nem küldjük ki innen: itt még a böngésző
            // alapértelmezése áll a tárolóban, és a végpont a felhasználó
            // SORÁBA is beírja — a belépés így felülírta a korábban választott
            // nyelvet, mielőtt bárki elolvasta volna. A döntés az `App.vue`-é,
            // ami a belépő MENTETT nyelvét ismeri.
            dispatch('user/authenticated', {}, {root: true}),
            dispatch('app/configuration', {}, {root: true}),
            dispatch('permissions')
        ]);
    },

    /** Kód kérése (`mail` | `sms`); a hibát (várakozás, sikertelen küldés) a hívó jeleníti meg. */
    twoFactorSend: async function ({state, commit}, channel) {
        const challenge = await state.service.twoFactorSend(channel);
        commit('SET_TWO_FACTOR', Object.assign({}, state.twoFactor, challenge));
        return challenge;
    },

    /** A kód ellenőrzése; jó kódnál bejelentkezve folytatja. A hibát a hívó jeleníti meg. */
    twoFactorVerify: async function ({state, commit, dispatch}, code) {
        const response = await state.service.twoFactorVerify(code);
        commit('SET_TWO_FACTOR', null);
        await dispatch('completeSignIn');
        return response;
    },

    /** Vissza a jelszóhoz: a függő belépés a szerveren magától lejár. */
    cancelTwoFactor: function ({commit}) {
        commit('SET_TWO_FACTOR', null);
    },

    /**
     * Belépés a Globalixból kapott tokennel. A végállapot ugyanaz, mint a jelszavas belépésé
     * (`login`): friss CSRF, az előző munkamenet kiléptetése, majd a felhasználó, a beállítások
     * és a jogosultságok betöltése. A hibát a hívó oldal jeleníti meg (a szerver üzenetét).
     */
    loginWithGlobalixToken: async function ({state, commit, dispatch}, token) {
        loading(commit)

        destroyLocal('accessToken')
        destroyLocal('permissions')
        destroyLocal('admin')
        commit('SET_PERMISSIONS', []);
        commit('SET_ADMIN', false)

        try {
            // Előtte NINCS kijelentkeztetés: a szerver a beváltáskor maga cseréli a munkamenetet
            // (Auth::login + regenerate). Egy kijelentkezett böngészőben a logout 401-et adott, amit
            // a globális kezelő lejárt munkamenetnek vett, és a login oldalra dobott.
            await state.service.csrf();
            const response = await state.service.loginWithGlobalixToken(token);

            commit('SET_AUTHENTICATED', true)
            storeLocal('authenticated', true);
            await Promise.all([
                dispatch('user/authenticated', {}, {root: true}),
                dispatch('app/configuration', {}, {root: true}),
                dispatch('permissions')
            ]);

            return response;
        } finally {
            loading(commit, false);
        }
    },

    /**
     * Belépés a Globalixba ágyazott keretbe (#273). Ugyanaz, mint a Globalix-beléptetés, de a keret
     * saját munkamenetébe (az axios az `X-XMED-Embed` fejlécet a beváltó oldaltól kezdve küldi), és a
     * keret állapota (menüpont, intézmény, a lejárati üzenet originjei) a fülhöz kötött tárolóba kerül.
     */
    loginWithGlobalixEmbedToken: async function ({state, commit, dispatch, rootState}, token) {
        loading(commit)

        destroyLocal('permissions')
        destroyLocal('admin')
        commit('SET_PERMISSIONS', []);
        commit('SET_ADMIN', false)

        try {
            await state.service.csrf();
            const response = await state.service.loginWithGlobalixEmbedToken(token);
            rememberEmbed(response);

            commit('SET_AUTHENTICATED', true)
            storeLocal('authenticated', true);
            await Promise.all([
                dispatch('user/authenticated', {}, {root: true}),
                dispatch('app/configuration', {}, {root: true}),
                dispatch('permissions')
            ]);
            // A munkamenet megmaradt-e: ha a böngésző a keret sütijét nem tárolta el (harmadik feles sütik
            // tiltva, pl. Safari a HTTP-s teszt-Globalix alatt), a beváltás sikerült, de a következő hívás már
            // névtelen. Ez belépési hiba, nem lejárat (utils/embed.js).
            const user = rootState.user?.authenticated;
            // (a tárolóból visszaállt, korábbi keret-felhasználó sem számít: az azonosítónak egyeznie kell)
            if (!user?.embed || Number(user.id) !== Number(response.user_id)) {
                const error = new Error('session_not_kept');
                error.response = {data: {
                    code: 'session_not_kept',
                    message: 'A böngésző nem tartotta meg a belépést a keretben (a harmadik féltől származó sütik tiltva vannak, pl. Safariban). Próbáld Chrome-mal vagy Edge-dzsel.',
                    origins: response.origins || null,
                }};
                throw error;
            }

            // a lejárati üzenet célja: a szerver listája az engedett Globalix-originekről
            rememberEmbed({...response, origins: user.embed.origins || [], confirmed: true});

            return response;
        } finally {
            loading(commit, false);
        }
    },

    logout: function ({state, commit, dispatch}) {
        loading(commit);
        commit('SET_PERMISSIONS', []);
        commit('SET_ADMIN', false)

        return state.service.logout().then(response => {
            commit('SET_AUTHENTICATED', false);
            dispatch('user/setAuthenticated', {}, {root: true})
            destroyLocal('authenticated')
            destroyLocal('permissions')
            destroyLocal('admin')
            destroyLocal('configuration')
            this.reset()
            return response
        }).catch((e) => {
            commit('SET_AUTHENTICATED', false);
            dispatch('user/setAuthenticated', {}, {root: true})
            destroyLocal('authenticated')
            destroyLocal('permissions')
            destroyLocal('admin')
            destroyLocal('configuration')
            this.reset();
            if (e?.response?.status !== 401) {
                handleError(e, commit)
            }
        }).finally(() => {
            loading(commit, false);
        });
    },

    logoutOnlyFrontend: function ({commit, dispatch}) {
        commit('SET_AUTHENTICATED', false);
        dispatch('user/setAuthenticated', {}, {root: true});
        destroyLocal('authenticated');
        destroyLocal('permissions');
        destroyLocal('admin');
        destroyLocal('configuration');
        this.reset();
    },

    // belépett böngészőben a lap előbb megkérdezi, kilépjen-e (signed-in-notice)
    forgotPassword: function ({state, commit, dispatch}, payload) {
        loading(commit)

        state.service.csrf().then(() => {
            return state.service.forgotPassword(payload).then((response) => {
                storeLocal('email', payload.email)
                loading(commit, false)
                dispatch('app/addAlert', {
                    message: response.message,
                    style: 'success',
                    icon: 'success',
                    countdown: 3000
                }, {root: true})
                return response.message
            }).catch((e) => {
                handleError(e, commit)
            })
        })
    },

    resetPassword: function ({state, commit, getters}, payload) {
        loading(commit)

        if (getters.authenticated) {
            loading(commit, false)
            return getters.user
        }

        const data = Object.assign({}, {email: getLocal('email')}, payload)

        return state.service.resetPassword(data).then((response) => {
            destroyLocal('email')
            loading(commit, false)
            return response.message
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    register: function ({state, commit, getters}, payload) {
        loading(commit)

        if (getters.authenticated) {
            loading(commit, false)
            const message = this.$t('auth.logged_in').ucFirst()
            alert(commit, message, 'warning', 'warning')
            throw new Error(message)
        }

        return state.service.csrf().then(() => {
            return state.service.register(payload).then((response) => {
                loading(commit, false)
                return response.data
            }).catch((e) => {
                handleError(e, commit)
            })
        })
    },

    permissions: function ({commit, state}) {
        loading(commit)

        return state.service.permissions().then(response => {
            loading(commit, false);
            commit('SET_PERMISSIONS', response.data);
            if (response.additional && response.additional.admin) {
                commit('SET_ADMIN', response.additional.admin);
            }
            return response.data
        }).catch((e) => {
            handleError(e, commit)
        })
    },

    // Validates the current user's token and refreshes it
    // with new data from the API.
    // eslint-disable-next-line no-unused-vars
    validate({ getters, dispatch, rootGetters }) {
        if (!rootGetters['user/authenticated']) return Promise.resolve(null)
        if (!getters.permissions) {
            dispatch('permissions')
        }
        return rootGetters['user/authenticated'];
    },
}
